import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, type Category } from '@/mocks/categories';
import { games } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'role-playing');
  const [visibleCount, setVisibleCount] = useState(8);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setVisibleCount(8);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) as Category;
  const categoryGames = games.filter((g) => g.category === activeCategory);

  const seoTitle = currentCategory ? `${currentCategory.name}游戏` : '游戏分类';
  const seoDesc = currentCategory ? currentCategory.description : '浏览黄游网页版所有游戏分类';

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`黄游网页版,${currentCategory?.name || '游戏'},在线游戏,网页游戏`}
        canonicalUrl={`https://huangyou.com/category/${activeCategory}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory?.name || '游戏分类'} - 黄游网页版`,
          description: seoDesc,
          url: `https://huangyou.com/category/${activeCategory}`,
        }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '游戏分类', href: '/category/role-playing' },
              { label: currentCategory?.name || '' },
            ]}
          />

          <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mt-4 mb-2">
            {currentCategory?.name || '游戏分类'}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 mb-8">
            {currentCategory?.description || ''} 共{categoryGames.length}款游戏
          </p>

          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
            {categoryGames.slice(0, visibleCount).map((game) => (
              <Link
                key={game.id}
                to={`/article/article-001`}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-200 cursor-pointer no-underline"
              >
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={game.imageUrl}
                    alt={game.title}
                    title={game.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {game.isNew && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
                      新游
                    </span>
                  )}
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs font-medium rounded-md flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    {game.rating}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-base font-heading text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors duration-200">
                    {game.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {game.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {game.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {game.players}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {visibleCount < categoryGames.length && (
            <div className="text-center mt-10">
              <button
                onClick={() => setVisibleCount((prev) => prev + 8)}
                className="px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                加载更多游戏
              </button>
            </div>
          )}

          {categoryGames.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-400">暂无该分类的游戏，敬请期待！</p>
              <Link
                to="/category/role-playing"
                className="inline-block mt-4 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览其他分类
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}